import React, { useEffect } from "react";
import { Grid, TextField } from "@mui/material";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";

import RegularButton from "components/CustomButtons/Button";
import { useForm } from "hooks/useForm";
import { orderArrayV2 } from "helpers/orderArray";
import { formatCustomDate } from 'helpers/dateTools'
import {
    createIssue,
    editIssue
} from "redux/actions/issues/issuesAction";

import { initialValues } from "./initialValues";

export const IssueForm = ({
    onClose,
    account = {}
}) => {
    const dispatch = useDispatch();
    const { selectedIssue } = useSelector(state => state.issues);
    const { stations, stationsList } = useSelector(({ companies }) => companies);
    const { unitsMap, units } = useSelector(({ unitsReducer }) => unitsReducer);
    const { isLoadingComp } = useSelector(({ search }) => search);

    const {
        inputValues,
        fieldErrors,
        handleInputOnChange,
        handleNewValues,
        runValidates
    } = useForm(initialValues.values, initialValues.required);
    const {
        issueId,
        statusOpt,
        serviceTypesOpt,
        unitsOpt,
        idStation,
        idUnit,
        service,
        accountCode,
        idAccount,
        serviceType,
        status,
        technician,
        observations,
        isFinished
    } = inputValues;

    const getNonEmptyObject = (obj1, obj2) => {
        const isEmpty = (obj) =>
            obj == null || (typeof obj === 'object' && Object.keys(obj).length === 0);

        if (!isEmpty(obj1)) return obj1;

        return obj2;
    }

    useEffect(() => {
        const newValues = getNonEmptyObject(selectedIssue, account)
        handleNewValues({
            issueId: newValues.id,
            idStation: stationsList.find(station => (station.id === newValues.idStation) || (station.label === newValues.stationName)),
            accountCode: newValues.code || newValues.customer?.account?.code,
            idAccount: newValues.customer?.account?.id || newValues.idAccount,
            status: statusOpt.find(status => status.value === newValues.status) || statusOpt[0],
            service: newValues.type,
            serviceType: serviceTypesOpt.find(serviceType => serviceType.value === newValues.serviceType),
            technician: newValues.technician,
            observations: newValues.observations,
            isFinished: ['RESOLVED', 'CANCELED'].includes(newValues.status),
            statusOpt: newValues.status == 'ACTIVE' ? statusOpt :
                newValues.status == 'ASSIGNED' ? statusOpt.filter(obj => obj.value != 'ACTIVE') :
                    [statusOpt.find(obj => obj.value == newValues.status)]
        })
    }, [])

    useEffect(() => {
        if (!idStation) return

        handleNewValues({ unitsOpt: unitsMap.filter(obj => obj.idStation === idStation?.id) })
    }, [idStation])

    useEffect(() => {
        const newValues = getNonEmptyObject(selectedIssue, account)
        if (newValues.idUnit || newValues.unit?.id)
            handleNewValues({ idUnit: unitsOpt.find(unit => (unit.id === newValues.idUnit) || (unit.id === newValues.unit?.id)) })
    }, [unitsOpt])

    const handleSubmit = (e) => {
        e.preventDefault();
        if (runValidates()) return

        const methodHandler = issueId ? editIssue : createIssue

        dispatch(methodHandler({
            id: inputValues.issueId,
            idUnit: inputValues.idUnit?.id,
            idStation: inputValues.idStation?.id,
            status: inputValues.status?.value,
            technician: inputValues.technician,
            observations: inputValues.observations,
            date: formatCustomDate(new Date(), "-"),
            idAccount: inputValues.idAccount,
            serviceType: inputValues.serviceType?.value,
        }, onClose))
    }

    return (
        <Grid container spacing={1} style={{ transition: "ease" }}>
            <Grid item xs={12}>
                <Autocomplete
                    id="issue-station-options"
                    options={orderArrayV2(stationsList || [])}
                    value={idStation}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Planta"
                            variant="outlined"
                            error={fieldErrors.idStation}
                            helperText={fieldErrors.idStation || ""}
                        />
                    )}
                    fullWidth
                    noOptionsText="No se encuentra la Planta"
                    disabled

                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="issue-units-options"
                    options={orderArrayV2(unitsOpt || [])}
                    value={idUnit}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Unidad"
                            variant="outlined"
                            error={fieldErrors.idUnit}
                            helperText={fieldErrors.idUnit || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ idUnit: v })}
                    noOptionsText="No se encuentra la Unidad"
                    disabled={isFinished}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Cuenta *"
                    name="accountCode"
                    value={inputValues.accountCode}
                    variant="outlined"
                    error={fieldErrors.accountCode}
                    helperText={fieldErrors.accountCode || ""}
                    onChange={handleInputOnChange}
                    disabled
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="issue-service-types-options"
                    options={orderArrayV2(serviceTypesOpt || [])}
                    value={serviceType}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tipo *"
                            variant="outlined"
                            error={fieldErrors.serviceType}
                            helperText={fieldErrors.serviceType || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ serviceType: v })}
                    noOptionsText="No se encuentra el Tipo"
                    disabled={issueId}
                />
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    id="issue-status-options"
                    options={orderArrayV2(statusOpt || [])}
                    value={status}
                    getOptionLabel={(option) => option?.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Estatus *"
                            variant="outlined"
                            error={fieldErrors.status}
                            helperText={fieldErrors.status || ""}
                        />
                    )}
                    fullWidth
                    onChange={(e, v) => handleNewValues({ status: v })}
                    noOptionsText="No se encuentra el estatus"
                    disabled={isFinished}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Técnico"
                    name="technician"
                    value={technician}
                    variant="outlined"
                    onChange={handleInputOnChange}
                    disabled={isFinished}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    label="Observaciones *"
                    name="observations"
                    value={observations}
                    variant="outlined"
                    error={fieldErrors.observations}
                    helperText={fieldErrors.observations || ""}
                    onChange={handleInputOnChange}
                    disabled={isFinished}
                />
            </Grid>
            <Grid
                item
                xs={12}
                style={{
                    marginTop: 30,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                }}
            >
                <RegularButton
                    color="danger"
                    onClick={onClose}
                    disabled={isLoadingComp?.loadingCreateIssue}
                >
                    Cancelar
                </RegularButton>
                <RegularButton
                    color="info"
                    onClick={handleSubmit}
                    loading={isLoadingComp?.loadingCreateIssue}
                    disabled={isFinished}
                >
                    Confirmar
                </RegularButton>
            </Grid>
        </Grid>
    );
};
